export function PageHeader({ overline, title, subtitle, actions }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
      <div>
        {overline && <div className="eyebrow mb-2">{overline}</div>}
        <h1 className="font-head text-3xl sm:text-4xl font-bold tracking-tighter">{title}</h1>
        {subtitle && <p className="text-neutral-500 mt-2 text-sm">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function Kpi({ label, value, hint, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#E5E5E5] p-5 hover:border-[#1F4E8C] transition-colors duration-200">
      <div className="eyebrow">{label}</div>
      <div className={`font-head text-4xl sm:text-5xl font-light tracking-tighter mt-3 ${accent ? "text-[#FF5500]" : "text-[#111]"}`}>{value}</div>
      {hint && <div className="text-xs text-neutral-500 mt-2">{hint}</div>}
    </div>
  );
}

export function Section({ title, right, children, className = "" }) {
  return (
    <div className={`bg-white border border-[#E5E5E5] ${className}`}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E5]">
        <h2 className="font-head text-lg font-semibold tracking-tight">{title}</h2>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

const STATUS_MAP = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  admin_approved: "bg-sky-50 text-sky-700 border-sky-200",
  submitted: "bg-amber-50 text-amber-700 border-amber-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  draft: "bg-neutral-100 text-neutral-700 border-neutral-300",
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Present: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Absent: "bg-red-50 text-red-700 border-red-200",
  "On Leave": "bg-sky-50 text-sky-700 border-sky-200",
  inactive: "bg-neutral-100 text-neutral-500 border-neutral-300",
  "Not Started": "bg-neutral-100 text-neutral-700 border-neutral-300",
  "On Hold": "bg-amber-50 text-amber-700 border-amber-200",
  Completed: "bg-sky-50 text-sky-700 border-sky-200",
  Cancelled: "bg-red-50 text-red-700 border-red-200",
};
const STATUS_LABEL = {
  admin_approved: "Admin Approved · Pending Super Admin",
  submitted: "Pending Admin",
  pending: "Pending Admin",
};

export function StatusBadge({ status, direct = false }) {
  const cls = STATUS_MAP[status] || "bg-neutral-100 text-neutral-700 border-neutral-300";
  const label = direct && status === "admin_approved" ? "Pending Super Admin" : (STATUS_LABEL[status] || status);
  return <span className={`inline-flex items-center text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 border ${cls}`}>{label}</span>;
}

const TL_META = {
  submitted: { label: "Submitted", dot: "#9CA3AF" },
  forwarded: { label: "Forwarded to Super Admin", dot: "#1F4E8C" },
  admin_approved: { label: "Approved by Admin", dot: "#0EA5E9" },
  approved: { label: "Final approved", dot: "#059669" },
  rejected: { label: "Rejected", dot: "#D92D20" },
  sent_back: { label: "Sent back to Admin", dot: "#F59E0B" },
};

export function ApprovalTimeline({ events }) {
  if (!events || events.length === 0)
    return <div className="text-xs text-neutral-400">No history yet.</div>;
  return (
    <ol className="relative border-l border-[#E5E5E5] ml-1 space-y-3" data-testid="approval-timeline">
      {events.map((e, i) => {
        const m = TL_META[e.action] || { label: e.action, dot: "#9CA3AF" };
        const when = e.at ? new Date(e.at).toLocaleString() : "";
        return (
          <li key={i} className="ml-4">
            <span className="absolute -left-[5px] w-2.5 h-2.5 rounded-full border border-white" style={{ background: m.dot }} />
            <div className="text-xs font-semibold text-neutral-800">{m.label}</div>
            <div className="text-[11px] text-neutral-500">
              {e.by ? `by ${e.by}` : ""}{when ? ` · ${when}` : ""}
            </div>
            {e.note ? <div className="text-[11px] text-neutral-600 italic mt-0.5">“{e.note}”</div> : null}
          </li>
        );
      })}
    </ol>
  );
}

export function healthFromPct(pct) {
  const p = Number(pct) || 0;
  if (p >= 100) return { tier: "critical", label: "Over Budget", cls: "bg-red-50 text-red-700 border-red-300", dot: "#D92D20" };
  if (p >= 80) return { tier: "warning", label: "At Risk", cls: "bg-amber-50 text-amber-700 border-amber-300", dot: "#F59E0B" };
  if (p > 0) return { tier: "healthy", label: "Healthy", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "#059669" };
  return { tier: "idle", label: "Not Started", cls: "bg-neutral-100 text-neutral-600 border-neutral-300", dot: "#9CA3AF" };
}

export function HealthBadge({ pct, showLabel = true }) {
  const h = healthFromPct(pct);
  return (
    <span data-testid={`health-badge-${h.tier}`} className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 border ${h.cls}`}>
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: h.dot }} />
      {showLabel ? h.label : null} {pct != null ? `${Math.round(pct)}%` : ""}
    </span>
  );
}

export function photoUrl(path) {
  if (!path) return null;
  return `${process.env.REACT_APP_BACKEND_URL}/api/profile-photo?path=${encodeURIComponent(path)}`;
}

export function Avatar({ name, photo, size = 40, className = "" }) {
  const dim = { width: size, height: size };
  const src = photoUrl(photo);
  if (src) {
    return <img src={src} alt={name || "avatar"} style={dim}
      className={`object-cover bg-neutral-100 ${className}`} data-testid="avatar-img" />;
  }
  return (
    <div style={{ ...dim, fontSize: Math.round(size * 0.42) }}
      className={`bg-[#1F4E8C] text-white grid place-items-center font-head font-semibold ${className}`}>
      {(name || "?")[0]?.toUpperCase()}
    </div>
  );
}

export function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}
export function fmtDur(mins) {
  if (!mins) return "0m";
  const h = Math.floor(mins / 60), m = Math.round(mins % 60);
  return h ? `${h}h ${m}m` : `${m}m`;
}
